/**
 * POS_CASHIER — the thu ngân face.
 *
 * Floor map → bill → order entry → payment → till. One shell, four views, no
 * URL routing between them: a cashier works from a fixed screen and a browser
 * back button in the middle of a payment is a hazard, not a feature.
 *
 * The floor map subscribes to the BranchHub (`tables` + `orders` topics, see
 * lib/realtime.ts) and refetches on every event; while the socket is live the
 * 8-second poll relaxes to a 60-second safety net. When the socket cannot be
 * established the poll carries the screen exactly as before.
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import { Button, ErrorState, Tabs } from '@sonauto/ui';
import { useApi, useAuth } from '../../auth/AuthProvider';
import { usePoll, useAsync } from '../../lib/hooks';
import { useRealtime } from '../../lib/realtime';
import { Chrome } from '../../components/Chrome';
import { FloorMap } from './FloorMap';
import { OrderEntry } from './OrderEntry';
import { SessionScreen } from './SessionScreen';
import { CashSessionScreen } from './CashSessionScreen';
import { loadCashSessionId, saveCashSessionId } from '../../lib/storage';
import type { AreasResponse, FloorMapResponse, FloorMapTable } from '../../lib/types';

type View =
  | { readonly kind: 'FLOOR' }
  | { readonly kind: 'SESSION'; readonly sessionId: string; readonly tableId: string | null }
  | {
      readonly kind: 'ORDER';
      readonly sessionId: string | null;
      readonly tableId: string | null;
      readonly appendToOrderId: string | null;
    }
  | { readonly kind: 'CASH' };

export function CashierShell({ onActivateDevice }: { readonly onActivateDevice: () => void }): React.JSX.Element {
  const api = useApi();
  const { me } = useAuth();
  const branchId = me?.session.activeBranchId ?? null;

  const [view, setView] = useState<View>({ kind: 'FLOOR' });
  const [cashSessionId, setCashSessionId] = useState<string | null>(() => loadCashSessionId());

  // Realtime table/order events → debounced refetch of the floor map. The
  // event payload is never trusted as state; it is only a "look again" signal.
  const reloadRef = useRef<() => void>(() => undefined);
  const debounceRef = useRef<number | undefined>(undefined);
  const queueReload = useCallback(() => {
    if (debounceRef.current !== undefined) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => reloadRef.current(), 250);
  }, []);
  const realtime = useRealtime({
    api,
    branchId,
    topics: ['tables', 'orders'],
    enabled: branchId !== null,
    onEvent: queueReload,
    onCatchUp: queueReload,
  });

  const floor = usePoll<FloorMapResponse>(
    (signal) => api.org.tables(branchId ?? '', undefined, { signal }),
    realtime.connected ? 60_000 : 8_000,
    [branchId, realtime.connected],
    { enabled: branchId !== null },
  );
  reloadRef.current = floor.reload;

  // `area.view` is not held by CASHIER_FLOOR, so a 403 here is expected and the
  // map simply renders ungrouped.
  const areas = useAsync<AreasResponse>((signal) => api.org.areas(branchId ?? '', { signal }), [branchId], {
    enabled: branchId !== null,
  });

  const tables = useMemo<readonly FloorMapTable[]>(() => floor.data?.tables ?? [], [floor.data]);

  const openTable = useCallback(
    async (table: FloorMapTable): Promise<void> => {
      if (table.session !== null) {
        setView({ kind: 'SESSION', sessionId: table.session.id, tableId: table.id });
        return;
      }
      if (branchId === null) return;
      const created = await api.orders.openSession({ branchId, tableId: table.id, guestCount: table.seats });
      floor.reload();
      setView({ kind: 'SESSION', sessionId: created.id, tableId: table.id });
    },
    [api, branchId, floor],
  );

  if (branchId === null) {
    return (
      <Chrome title="SonAuto POS" onActivateDevice={onActivateDevice}>
        <ErrorState
          title="Chưa chọn chi nhánh"
          message="Tài khoản này chưa gắn với chi nhánh nào đang hoạt động. Chọn chi nhánh trong menu tài khoản."
        />
      </Chrome>
    );
  }

  const activeTable =
    view.kind === 'SESSION' || view.kind === 'ORDER'
      ? (tables.find((t) => t.id === view.tableId) ?? null)
      : null;

  return (
    <Chrome
      title={view.kind === 'CASH' ? 'Ca quỹ' : 'Thu ngân'}
      onActivateDevice={onActivateDevice}
      actions={
        <Tabs
          items={[
            { id: 'FLOOR', label: 'Sơ đồ bàn' },
            { id: 'CASH', label: 'Ca quỹ', badge: cashSessionId === null ? 'chưa mở' : 'đang mở' },
          ]}
          value={view.kind === 'CASH' ? 'CASH' : 'FLOOR'}
          onChange={(id) => setView(id === 'CASH' ? { kind: 'CASH' } : { kind: 'FLOOR' })}
          variant="pill"
          ariaLabel="Khu vực làm việc"
        />
      }
    >
      {view.kind === 'FLOOR' ? (
        <div className="flex h-full flex-col">
          <FloorMap
            tables={tables}
            areas={areas.data?.areas ?? []}
            loading={floor.initialLoading}
            error={floor.error === null ? null : floor.error.message}
            onRetry={floor.reload}
            onSelect={(table) => void openTable(table)}
          />
          <div className="flex shrink-0 items-center gap-2 border-t border-line bg-surface px-3 py-2">
            <Button
              size="lg"
              onClick={() => setView({ kind: 'ORDER', sessionId: null, tableId: null, appendToOrderId: null })}
            >
              Đơn mang về
            </Button>
            <span className="text-xs text-fg-subtle">
              {floor.lastUpdatedAt === null ? '' : `Cập nhật ${new Date(floor.lastUpdatedAt).toLocaleTimeString('vi-VN')}`}
              {realtime.connected ? ' · realtime' : ''}
            </span>
          </div>
        </div>
      ) : null}

      {view.kind === 'SESSION' ? (
        <SessionScreen
          branchId={branchId}
          sessionId={view.sessionId}
          table={activeTable}
          tables={tables}
          cashSessionId={cashSessionId}
          onBack={() => setView({ kind: 'FLOOR' })}
          onAddItems={(orderId) =>
            setView({
              kind: 'ORDER',
              sessionId: view.sessionId,
              tableId: view.tableId,
              appendToOrderId: orderId,
            })
          }
          onChanged={floor.reload}
        />
      ) : null}

      {view.kind === 'ORDER' ? (
        <OrderEntry
          branchId={branchId}
          diningSessionId={view.sessionId}
          tableId={view.tableId}
          tableLabel={activeTable?.displayName ?? 'Mang về'}
          appendToOrderId={view.appendToOrderId}
          onDone={() => {
            floor.reload();
            setView(
              view.sessionId === null
                ? { kind: 'FLOOR' }
                : { kind: 'SESSION', sessionId: view.sessionId, tableId: view.tableId },
            );
          }}
          onCancel={() =>
            setView(
              view.sessionId === null
                ? { kind: 'FLOOR' }
                : { kind: 'SESSION', sessionId: view.sessionId, tableId: view.tableId },
            )
          }
        />
      ) : null}

      {view.kind === 'CASH' ? (
        <div className="h-full overflow-y-auto">
          <CashSessionScreen
            branchId={branchId}
            cashSessionId={cashSessionId}
            onChange={(id) => {
              saveCashSessionId(id);
              setCashSessionId(id);
            }}
          />
        </div>
      ) : null}
    </Chrome>
  );
}
